/** Flags locales (AsyncStorage) que gobiernan las cards de activación. */
export type StoreActivationFlags = {
  storeDismissed: boolean;
  onlineDismissed: boolean;
  weekOneDismissed: boolean;
  teamSkipped: boolean;
  mpSkipped: boolean;
  essentialsSaved: boolean;
  /** Usuario marcó "no tengo impresora" en el paso de semana 1. */
  printerSkipped: boolean;
  /** La compu ya tiene la térmica configurada en modo impresión directa. */
  printerReady: boolean;
};

/**
 * Veredicto "setup online completo" desde hechos de `home_snapshot.storefront`
 * (pura, testeable). Replica `useOnlineOperatingSetup`: publicada + delivery
 * resuelto. La bandeja es informativa — se llena sola con el primer pedido,
 * no es un paso que el usuario pueda completar. MP es opcional → no bloquea.
 * `null` = no se puede determinar (campos ausentes en un server viejo o
 * sección fallida) → el gating carga el bundle por las dudas.
 */
export function resolveOnlineSetupComplete(input: {
  isPublic: boolean | null;
  deliveryReady: boolean | null;
  /** `delivery_fulfillment` capability — sin ella el paso delivery no aplica. */
  hasDelivery: boolean;
}): boolean | null {
  if (input.isPublic !== true) return null;
  const { deliveryReady, hasDelivery } = input;
  if (hasDelivery && deliveryReady === null) {
    return null;
  }
  return !hasDelivery || deliveryReady === true;
}

/**
 * Decide si hace falta `store_activation_snapshot` (pura, testeable).
 * - `!is_public` y checklist no dismissido → sí (la card se muestra).
 * - `is_public`, setup-online no dismissido y NO completo → sí (la card
 *   solo puede renderizar si falta algo; completa ⇒ nunca se muestra).
 * - semana-1 con ventas, no dismissida y con pasos que necesitan evidencia
 *   remota (empleados/invitaciones/MP) → sí.
 */
export function shouldLoadStoreActivationEvidence(input: {
  baseEligible: boolean;
  /** false = home_snapshot no respondió (fail-soft: no cargar). */
  snapshotLoaded: boolean;
  isPublic: boolean | null;
  lifetimeSales: number | null;
  /** resolveOnlineSetupComplete; null = desconocido → carga por las dudas. */
  onlineSetupComplete: boolean | null;
  flags: StoreActivationFlags;
  mpPlanAllowed: boolean;
}): boolean {
  if (!input.baseEligible || !input.snapshotLoaded) return false;
  const { isPublic, lifetimeSales, onlineSetupComplete, flags: f, mpPlanAllowed } = input;
  return (
    (isPublic === false && !f.storeDismissed) ||
    (isPublic === true && !f.onlineDismissed && onlineSetupComplete !== true) ||
    ((lifetimeSales ?? 0) >= 1 &&
      !f.weekOneDismissed &&
      (!f.teamSkipped || (mpPlanAllowed && !f.mpSkipped)))
  );
}
